import { clamp } from '#shared'
import { useSettingIds } from './setting-row.tsx'
import styles from './slider.module.css'

interface SliderProps {
  readonly min: number
  readonly max: number
  readonly step: number
  readonly value: number
  readonly format: (value: number) => string
  readonly onChange: (value: number) => void
  /** Fired when a pointer or arrow key is released, for a short preview. */
  readonly onRelease?: () => void
}

function snap(value: number, step: number, min: number, max: number): number {
  const snapped = Math.round(value / step) * step
  return clamp(Number(snapped.toFixed(2)), min, max)
}

/**
 * A styled range input. Arrow keys are handled here because a synthetic key event
 * (gamepad left/right) does not move a native slider.
 */
export function Slider({ min, max, step, value, format, onChange, onRelease }: SliderProps) {
  const { id, describedBy } = useSettingIds()
  const text = format(value)

  const stepBy = (direction: number): void => {
    onChange(snap(value + direction * step, step, min, max))
  }

  return (
    <div class={styles.slider}>
      <input
        id={id}
        class={styles.input}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-valuetext={text}
        aria-describedby={describedBy}
        onInput={(event) => {
          onChange(snap(Number(event.currentTarget.value), step, min, max))
        }}
        onKeyDown={(event) => {
          const direction =
            event.key === 'ArrowRight' || event.key === 'ArrowUp'
              ? 1
              : event.key === 'ArrowLeft' || event.key === 'ArrowDown'
                ? -1
                : 0
          if (direction === 0) return
          event.preventDefault()
          stepBy(direction)
        }}
        onKeyUp={(event) => {
          if (event.key.startsWith('Arrow')) onRelease?.()
        }}
        onPointerUp={() => {
          onRelease?.()
        }}
      />
      <span class={styles.value}>{text}</span>
    </div>
  )
}
